import { List, ListItem, ListItemIcon, Stack, SxProps, Theme } from '@mui/material';
import { ShowIf } from '@/utils/ui/showIf';

interface FormErrorSummaryProps {
  errors: any;
  sx?: SxProps<Theme>;
}

/**
 * Recursively extracts all error messages from nested validation errors
 * @param error - Validation error from react-hook-form
 * @returns Array of formatted error messages
 */
const getAllErrorMessages = (error: any): string[] => {
  const messages: string[] = [];

  if (!error) return messages;

  if (typeof error === 'string') {
    return [error];
  }

  if (error.message) {
    return [String(error.message)];
  }

  // If it is an object, iterate over its properties
  if (typeof error === 'object') {
    Object.entries(error).forEach(([key, value]: [string, any]) => {
      if (value && typeof value === 'object') {
        // If it has a message, extract it
        if (value.message) {
          messages.push(String(value.message));
        } else {
          // Recursively search in nested objects
          const nestedMessages = getAllErrorMessages(value);
          messages.push(...nestedMessages);
        }
      }
    });
  }

  return messages;
};

export const FormErrorSummary = ({ errors, sx }: FormErrorSummaryProps) => {
  const messages = getAllErrorMessages(errors);

  return (
    <ShowIf condition={!!Object.keys(errors).length}>
      <Stack
        alignItems="flex-start"
        sx={{
          mb: 1,
          mt: 5,
          bgcolor: 'error.lighter',
          p: 2,
          borderRadius: 1,
          ...sx,
        }}
      >
        <List sx={{ width: '100%' }}>
          {messages.map((message: string, index: number) => (
            <ListItem
              key={`error-${index}`}
              sx={{
                py: 0.75,
                color: 'error.main',
                '&:before': { content: '"•"', mr: 1.5, fontWeight: 'bold' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 'auto' }}></ListItemIcon>
              <span style={{ marginLeft: 0 }}>{message}</span>
            </ListItem>
          ))}
        </List>
      </Stack>
    </ShowIf>
  );
};
